import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ClinicianNavigation from './ClinicianNavigation';
import PatientsPage from './PatientsPage';
import Surveys from './Surveys';
import Triage from './Triage';
import Education from './Education';
import Reports from './Reports';
import Account from './Account';

import '../styles/ClinicianDashboard.css';

const ClinicianDashboard = ({ children }) => {
  const { currentUser, logout, patients, fetchPatients, setPatients } = useAuth();
  const [error, setError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('patients');
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch patients when component mounts and when currentUser changes
  useEffect(() => {
    console.log('=== CLINICIAN DASHBOARD EFFECT RUNNING ===');
    console.log('Current user:', currentUser);
    
    let isMounted = true;
    
    const loadPatients = async () => {
      console.log('=== STARTING LOAD PATIENTS ===');
      console.log('Current user ID:', currentUser?.uid);
      console.log('Current user role:', currentUser?.role);
      
      if (!currentUser) {
        console.log('No current user, clearing patients');
        if (isMounted) {
          setPatients([]);
          setIsLoading(false);
        }
        return;
      }
      
      if (currentUser.role !== 'clinician') {
        console.log('Current user is not a clinician, not loading patients');
        if (isMounted) {
          setPatients([]);
          setIsLoading(false);
        }
        return;
      }
      
      try {
        console.log('Current user ID:', currentUser.uid);
        console.log('Setting loading to true');
        
        if (isMounted) {
          setIsLoading(true);
        }
        
        console.log('Calling fetchPatients...');
        const patients = await fetchPatients();
        console.log('fetchPatients completed, result:', patients);
        
        if (isMounted) {
          console.log('Updating patients state with:', patients);
          setPatients(patients || []);
        }
      } catch (error) {
        console.error('Error in loadPatients:', error);
        if (isMounted) {
          const errorMessage = error.message || 'Failed to load patients. Please refresh the page.';
          console.error('Setting error:', errorMessage);
          setError(errorMessage);
        }
      } finally {
        console.log('In finally block, setting loading to false');
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    console.log('Calling loadPatients...');
    loadPatients().catch(error => {
      console.error('Unhandled error in loadPatients:', error);
      if (isMounted) {
        setError('An unexpected error occurred. Please check the console for details.');
        setIsLoading(false);
      }
    });
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      console.log('=== CLEANUP CLINICIAN DASHBOARD EFFECT ===');
      isMounted = false;
    };
  }, [currentUser?.uid, fetchPatients, setPatients, setError]);

  // Update active section based on URL
  useEffect(() => {
    console.log('🔗 URL changed:', location.pathname);
    const pathParts = location.pathname.split('/').filter(Boolean);
    let section = 'patients'; // default
    
    console.log('Path parts:', pathParts);
    
    if (pathParts[0] === 'clinician') {
      console.log('Detected clinician route');
      if (pathParts[1] === 'patients') {
        console.log('Detected patients section');
        section = 'patients';
      } else if (pathParts[1]) {
        console.log('Detected section:', pathParts[1]);
        section = pathParts[1];
      }
    }
    
    console.log('Setting active section to:', section);
    setActiveSection(section);
  }, [location]);

  // Handle section change - update URL and state
  const handleSectionChange = (section) => {
    console.log('📌 Section change requested:', section);
    setActiveSection(section);
    const newPath = `/clinician/${section}`;
    console.log('Navigating to:', newPath);
    navigate(newPath);
  };

  // Render content based on active section
  const renderContent = () => {
    console.log('🎨 Rendering content for section:', activeSection);
    
    // If we have children (like PatientProfilePage), render them instead
    if (React.Children.count(children) > 0) {
      console.log('👶 Rendering children:', children);
      return children;
    }
    
    console.log('🔍 No children, rendering section:', activeSection);
    switch (activeSection) {
      case 'patients':
        return <PatientsPage />;
      case 'surveys':
        return <Surveys />;
      case 'triage':
        return <Triage />;
      case 'education':
        return <Education />;
      case 'reports':
        return <Reports />;
      case 'account':
        return <Account />;
      default:
        console.log('⚠️ Unknown section, defaulting to PatientsPage');
        return <PatientsPage />;
    }
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="logo-container">
            <img src={process.env.PUBLIC_URL + '/logo.png'} alt="Logo" className="header-logo" />
            {/* <h1>Healthcare Platform</h1> */}
          </div>
          <div className="user-info">
            <span 
              className="username-link" 
              onClick={() => setActiveSection('account')}
            >
              Welcome, {currentUser.firstName} {currentUser.lastName}
            </span>
            <button onClick={logout} className="logout-btn">Logout</button>
          </div>
        </div>
      </header>

      <ClinicianNavigation 
        activeSection={activeSection} 
        onSectionChange={handleSectionChange}
      />

      <main className="dashboard-main">
        <div className="dashboard-content">
          {children || (
            <>
              {activeSection === 'patients' && <PatientsPage />}
              
              {activeSection === 'surveys' && <Surveys />}
              {activeSection === 'triage' && <Triage />}
              {activeSection === 'education' && <Education />}
              {activeSection === 'reports' && <Reports />}
              {activeSection === 'account' && <Account />}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ClinicianDashboard;
